import { ContentService } from "../services/content.service.js";
import { SearchService } from "../services/search.service.js";
import { app } from "../firebase-config.js";
import { SearchBar } from "../components/feature/home/SearchBar.js";
import { TrendingSection } from "../components/feature/home/TrendingSection.js";
import { NewArrivalsSection } from "../components/feature/home/NewArrivalsSection.js";
import { TagCloudSection } from "../components/feature/home/TagCloudSection.js";
import { SearchResultsGrid } from "../components/feature/home/SearchResultsGrid.js";

// [NEW] Modular Components
import { HeroBanner } from "../components/feature/home/HeroBanner.js";
import { ContinueReadingSection } from "../components/feature/home/ContinueReadingSection.js";
import { CuratedSection } from "../components/feature/home/CuratedSection.js";
import { GrandGallerySection } from "../components/feature/home/GrandGallerySection.js";
import { locale } from "../services/LocaleService.js";

export const HomePage = async () => {
    // 0. Setup Layout
    const container = document.createElement('div');
    container.className = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 relative';

    // State Initialization
    const state = {
        isSearching: false,
        query: '',
        searchResults: [],
        items: {
            trending: [],
            newArrivals: [],
            curated: [],
            banners: [],
            hallOfFame: []
        },
        loading: {
            banners: true,
            trending: true,
            newArrivals: true,
            curated: true,
            hallOfFame: true,
            history: true
        }
    };

    // 1. Header (Static)
    const header = document.createElement('div');
    header.className = 'flex justify-center items-center mb-6 w-full';
    header.innerHTML = `
        <div class="text-gray-500 text-sm font-medium animate-fade-in select-none flex items-center gap-2">
            ${locale.t('main.banner_welcome')}
        </div>
    `;
    container.appendChild(header);

    // 2. Search Bar (Static)
    // [FIX] Support Display Query for UI (Results Header)
    const handleSearch = async (query, displayQuery = null) => {
        state.query = displayQuery || query;
        state.isSearching = query.length > 0;

        // Toggle Visibility
        const contentArea = container.querySelector('#home-content-area');
        const searchArea = container.querySelector('#home-search-area');

        if (state.isSearching) {
            contentArea.classList.add('hidden');
            searchArea.classList.remove('hidden');

            // Render Loading State for Search
            searchArea.innerHTML = renderSkeleton('grid');

            try {
                // [Async] Server-side Search
                state.searchResults = await SearchService.searchSeries(query);
            } catch (e) {
                console.error("Search Fail", e);
                state.searchResults = [];
            }

            // Render Results
            searchArea.innerHTML = '';
            searchArea.appendChild(SearchResultsGrid(state.searchResults, state.query));
        } else {
            contentArea.classList.remove('hidden');
            searchArea.classList.add('hidden');
        }
    };

    const searchComp = SearchBar(handleSearch);

    // Hash Query Check
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(hash.split('?')[1] || '');
    const initialQuery = searchParams.get('q') || '';

    container.appendChild(searchComp.element);

    // 3. Search Results Area (Hidden by default)
    const searchResultArea = document.createElement('div');
    searchResultArea.id = 'home-search-area';
    searchResultArea.className = 'hidden min-h-[60vh]';
    container.appendChild(searchResultArea);

    // 4. Main Content Area (Progressive Rendering Container)
    const contentArea = document.createElement('div');
    contentArea.id = 'home-content-area';
    contentArea.className = "flex flex-col min-h-[60vh] transition-opacity duration-300 gap-12 pb-20"; // [FIX] Uniform Spacing
    container.appendChild(contentArea);

    // [Helper] Skeleton Generators
    const renderSkeleton = (type) => {
        const pulse = 'animate-pulse bg-gray-200 dark:bg-zinc-800 rounded-xl';
        if (type === 'banner') return `<div class="${pulse} w-full aspect-[21/9] md:aspect-[3/1]"></div>`;
        if (type === 'list') return `
            <div class="flex gap-4 overflow-hidden">
                ${[1, 2, 3, 4].map(() => `<div class="${pulse} w-32 h-48 flex-shrink-0"></div>`).join('')}
            </div>`;
        if (type === 'hallOfFame') return `
            <div class="${pulse} h-8 w-48 mb-4"></div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                ${[1, 2, 3].map(() => `<div class="${pulse} h-24 rounded-xl"></div>`).join('')}
            </div>`;
        if (type === 'grid') return `
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                 ${[1, 2, 3, 4, 5, 6, 7, 8].map(() => `<div class="${pulse} h-64 rounded-xl"></div>`).join('')}
            </div>`;
        return `<div class="${pulse} w-full h-32"></div>`;
    };

    // [Helper] Create Section Container
    const createSection = (id, skeletonType) => {
        const div = document.createElement('div');
        div.id = id;
        div.innerHTML = renderSkeleton(skeletonType);
        return div;
    };

    // [Helper] Spacing Normalizer
    const cleanSection = (el) => {
        if (!el) return el;
        el.classList.remove('mb-2', 'mb-4', 'mb-8', 'mb-10', 'mt-10', 'my-8', 'my-10');
        return el;
    };

    // 5. Initial Skeleton Render (Instant!)

    // 5.1 Hero Banner
    contentArea.appendChild(createSection('section-banners', 'banner'));

    // 5.2 Empty Shelf (Taste Sniper)
    const recWidget = document.createElement('div');
    recWidget.className = "px-1 md:px-0 animate-fade-in"; // Removed mb-2
    recWidget.innerHTML = `
         <div class="relative w-full rounded-2xl overflow-hidden shadow-lg group cursor-pointer border border-zinc-100 dark:border-zinc-800" onclick="window.router.navigate('/library/recommendation')">
            <div class="absolute inset-0 bg-zinc-900">
                 <img src="/login.webp" class="w-full h-full object-cover opacity-50 transform scale-100 group-hover:scale-105 transition-transform duration-700" alt="Empty Shelf">
                 <div class="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent"></div>
            </div>
            <div class="relative z-10 p-6 md:p-8 flex flex-col items-start justify-center h-full">
                 <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-fuchsia-300 text-xs font-bold mb-3 shadow-lg">
                    <span class="material-symbols-outlined text-sm animate-pulse">auto_awesome</span> ${locale.t('recommendation.label_empty_shelf')}
                 </div>
                 <h2 class="text-2xl md:text-3xl font-bold text-white mb-2 font-display">
                    ${locale.t('recommendation.widget_title')}
                 </h2>
                 <p class="text-white/70 text-sm md:text-base font-light mb-6 max-w-md">
                    ${window.user
            ? locale.t('recommendation.widget_desc', { name: window.user.displayName })
            : locale.t('recommendation.widget_desc_guest')}
                 </p>
                 <button class="px-6 py-2.5 bg-white text-black text-sm font-bold rounded-xl hover:bg-zinc-100 transition-colors shadow-xl">
                    ${locale.t('recommendation.btn_check')} <span class="ml-1">→</span>
                 </button>
            </div>
         </div>
    `;
    contentArea.appendChild(recWidget);

    // 5.3 Hall of Fame
    contentArea.appendChild(createSection('section-hall-of-fame', 'hallOfFame'));

    // 5.4 Curated
    contentArea.appendChild(createSection('section-curated', 'list'));

    // 5.5 History (Async & Optional)
    const historySection = document.createElement('div');
    historySection.id = 'section-history';
    contentArea.appendChild(historySection);

    // 5.6 Trending
    contentArea.appendChild(createSection('section-trending', 'list'));

    // 5.7 New Arrivals
    contentArea.appendChild(createSection('section-new-arrivals', 'list'));

    // 5.8 Tag Cloud
    const tagCloudSection = document.createElement('div');
    tagCloudSection.id = 'section-tag-cloud';
    contentArea.appendChild(tagCloudSection);

    // 5.9 Plaza Widget
    const plazaDiv = document.createElement('div');
    plazaDiv.id = 'section-plaza';
    contentArea.appendChild(plazaDiv);

    // 5.10 Grand Gallery
    contentArea.appendChild(createSection('section-grand-gallery', 'grid'));


    // 6. Progressive Data Fetching
    const { getFirestore, collection, query, where, orderBy, getDocs, limit } = await import("firebase/firestore");
    const db = getFirestore(app);
    const { AdminService } = await import('../services/admin.service.js');

    // 6.1 Banners
    (async () => {
        try {
            const snap = await getDocs(query(collection(db, 'banners'), orderBy('order', 'asc'), limit(5)));
            state.items.banners = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            const el = container.querySelector('#section-banners');
            if (el) {
                el.innerHTML = '';
                el.appendChild(cleanSection(HeroBanner(state.items.banners)));
            }
        } catch (e) { console.warn("Banner Fetch Failed", e); }
    })();

    // 6.2 Hall of Fame
    (async () => {
        try {
            state.items.hallOfFame = await AdminService.runWeeklyHallOfFame();

            const el = container.querySelector('#section-hall-of-fame');
            if (el) {
                el.innerHTML = '';
                el.appendChild(cleanSection(createHallOfFameElement(state.items.hallOfFame)));
            }
        } catch (e) { console.warn("HOF Fetch Failed", e); }
    })();

    // 6.3 Curated (Editor's Pick)
    (async () => {
        try {
            // [FIX] Added 'status' filter to comply with Security Rules (Non-admins can only read published)
            const snap = await getDocs(query(
                collection(db, 'series'),
                where('isCurated', '==', true),
                where('status', '==', 'published'),
                limit(20)
            ));
            state.items.curated = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (a.priority || 999) - (b.priority || 999))
                .slice(0, 5);

            const el = container.querySelector('#section-curated');
            if (el) {
                if (state.items.curated.length > 0) {
                    el.innerHTML = '';
                    el.appendChild(cleanSection(CuratedSection(state.items.curated)));
                } else {
                    el.remove(); // Remove skeleton if empty
                }
            }
        } catch (e) {
            console.warn("Curated Fetch Failed", e);
            // Remove skeleton on error to prevent ghost gap
            const el = container.querySelector('#section-curated');
            if (el) el.remove();
        }
    })();

    // 6.4 History (Legacy)
    if (window.user) {
        (async () => {
            try {
                // Ensure migration
                import('../services/history.service.js').then(({ HistoryService }) => {
                    HistoryService.migrateLegacyHistory(window.user.uid);
                });

                const historyEl = await ContinueReadingSection(window.user);
                const el = container.querySelector('#section-history');
                if (el) {
                    el.innerHTML = '';
                    if (historyEl.children.length > 0) {
                        el.appendChild(cleanSection(historyEl));
                    }
                }
            } catch (e) { console.warn("History Fetch Failed", e); }
        })();
    }

    // 6.5 Trending
    (async () => {
        try {
            state.items.trending = await ContentService.getTrendingSeries(10);
            const el = container.querySelector('#section-trending');
            if (el) {
                el.innerHTML = '';
                const { TrendingSection } = await import('../components/feature/home/TrendingSection.js');
                el.appendChild(cleanSection(TrendingSection(state.items.trending)));
            }
        } catch (e) { console.warn("Trending Fetch Failed", e); }
    })();

    // 6.6 New Arrivals
    (async () => {
        try {
            state.items.newArrivals = await ContentService.getNewArrivals(10);
            const el = container.querySelector('#section-new-arrivals');
            if (el) {
                el.innerHTML = '';
                const { NewArrivalsSection } = await import('../components/feature/home/NewArrivalsSection.js');
                el.appendChild(cleanSection(NewArrivalsSection(state.items.newArrivals)));
            }
        } catch (e) { console.warn("New Arrivals Fetch Failed", e); }
    })();

    // 6.7 Tag Cloud
    const elTag = container.querySelector('#section-tag-cloud');
    if (elTag) {
        // TagCloudSection is synchronous usually, check implementation if async needed
        // Assuming sync for simplicity based on previous existing code structure
        import('../components/feature/home/TagCloudSection.js').then(({ TagCloudSection }) => {
            elTag.appendChild(cleanSection(TagCloudSection((tagData) => {
                // [FIX] Support object for localized display ({ value: '판타지', label: 'Fantasy' })
                const query = typeof tagData === 'object' ? tagData.value : tagData;
                const display = typeof tagData === 'object' ? tagData.label : tagData;

                searchComp.input.value = display; // Show localized name
                searchComp.input.focus();
                searchComp.element.querySelector('#btn-search-clear')?.classList.remove('hidden');
                handleSearch(query, display); // Pass display label to update Grid Header
            })));
        });
    }

    // 6.8 Plaza Widget
    import('../components/feature/plaza/PlazaFlow.js').then(({ PlazaFlow }) => {
        const elPlaza = container.querySelector('#section-plaza');
        if (elPlaza) {
            // PlazaFlow renders itself into elPlaza?
            // Usually `new PlazaFlow(el).render()` appends content.
            // We can't easily strip margins if it renders internal structure.
            // But PlazaFlow usually occupies full width. 
            // We just ensure elPlaza doesn't have extra margins.
            // It is a div created with id='section-plaza' which is clean.
            new PlazaFlow(elPlaza).render();
        }
    });

    // 6.9 Grand Gallery (Grid)
    (async () => {
        try {
            const { GrandGallerySection } = await import('../components/feature/home/GrandGallerySection.js');
            const el = container.querySelector('#section-grand-gallery');
            if (el) {
                el.innerHTML = '';
                el.appendChild(cleanSection(GrandGallerySection()));
            }
        } catch (e) { console.warn("Grand Gallery Load Failed", e); }
    })();

    // 7. Handle Initial Search (if provided)
    if (initialQuery) {
        // Switch to search mode immediately
        searchComp.input.value = initialQuery;
        setTimeout(() => searchComp.element.querySelector('#btn-search-clear')?.classList.remove('hidden'), 0);
        handleSearch(initialQuery);
    }

    // 8. Event Subscription (Golden Time) - Deferred
    // We can run this after banners/trending are loaded, or listen immediately.
    // For simplicity, we add listener but it needs to know how to update the ALREADY rendered sections.
    // Since we rendered them as DOM elements, we can inspect and update them? 
    // Or just re-render the specific components. 
    // The previous architecture re-rendered the WHOLE page.
    // Better strategy for V2: Just let the components handle their own badges or re-render sections.
    // For now, let's keep it simple: When event info arrives, we re-inject into data and re-render sections.

    (async () => {
        const { EventService } = await import('../services/event.service.js');
        EventService.subscribe((events) => {
            const activeEvents = EventService.getActiveGoldenTimeEvents();
            const eventMap = {};
            activeEvents.forEach(evt => eventMap[evt.seriesId] = evt);

            // Update Data
            const inject = (list) => {
                if (!list) return;
                list.forEach(s => {
                    if (eventMap[s.id]) {
                        s._marketing = { multiplier: eventMap[s.id].multiplier, theme: EventService.getEventTheme(eventMap[s.id].multiplier) };
                    } else delete s._marketing;
                });
            };
            inject(state.items.trending);
            inject(state.items.newArrivals);
            inject(state.items.curated);

            state.items.banners.forEach(b => {
                const linked = Object.keys(eventMap).find(id => b.linkUrl?.includes(id));
                if (linked) b._eventBadge = `EVENT x${eventMap[linked].multiplier}`;
                else delete b._eventBadge;
            });

            // Re-render relevant sections if they exist
            const refresh = (id, Component, data) => {
                const el = container.querySelector(id);
                if (el && data && data.length > 0) {
                    el.innerHTML = '';
                    el.appendChild(Component(data, false));
                }
            };

            refresh('#section-trending', TrendingSection, state.items.trending);
            refresh('#section-new-arrivals', NewArrivalsSection, state.items.newArrivals);
            refresh('#section-curated', CuratedSection, state.items.curated);
            refresh('#section-banners', HeroBanner, state.items.banners);
        });
    })();

    // Load Popups (Low Priority)
    setTimeout(() => {
        import('../components/main-popup-manager.js').then(({ MainPopupManager }) => MainPopupManager.init(document.body));
    }, 1000);

    return container;

    // Helper: Hall of Fame Render (Inline)
    function createHallOfFameElement(list) {
        const div = document.createElement('div');
        div.className = "mb-10 animate-fade-in";

        // [Guest/Error State] If list is empty, show placeholder
        if (!list || list.length === 0) {
            div.innerHTML = `
                <div class="flex items-center gap-2 mb-4 px-1 md:px-0">
                    <span class="material-symbols-outlined text-amber-500">trophy</span>
                    <h2 class="text-xl font-bold text-gray-900">${locale.t('main.hall_of_fame')}</h2>
                </div>
                <div class="bg-gradient-to-br from-amber-50 to-white border border-amber-100 p-8 rounded-xl flex flex-col items-center justify-center text-center shadow-sm">
                    <div class="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                        <span class="material-symbols-outlined text-3xl text-amber-500">lock</span>
                    </div>
                    <h3 class="text-lg font-bold text-gray-900 mb-2">${locale.t('main.hall_of_fame_locked')}</h3>
                    <p class="text-sm text-gray-500 mb-6 max-w-sm">${locale.t('main.hall_of_fame_desc')}</p>
                    <button onclick="window.router.navigate('/login')" class="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-colors shadow-md shadow-amber-200">
                        ${locale.t('main.login_btn')}
                    </button>
                </div>
            `;
            return div;
        }

        div.innerHTML = `
            <div class="flex items-center gap-2 mb-4 px-1 md:px-0">
                <span class="material-symbols-outlined text-amber-500">trophy</span>
                <h2 class="text-xl font-bold text-gray-900">${locale.t('main.hall_of_fame')}</h2>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                ${list.map((u, idx) => {
            const isSeries = u.type === 'series';
            const link = isSeries ? `/series/${u.uid}` : `/user/${u.uid}`;
            return `
                        <div class="bg-gradient-to-br from-white to-amber-50/50 border border-amber-100 p-4 rounded-xl flex items-center gap-4 shadow-sm relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onclick="window.router.navigate('${link}')">
                        <div class="absolute -right-2 -top-2 text-6xl text-amber-100 font-black opacity-50 z-0">${u.rank || idx + 1}</div>
                        <div class="relative z-10 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-2xl shadow-inner border-2 border-white overflow-hidden">
                            ${u.displayImage ? `<img src="${u.displayImage}" class="w-full h-full object-cover" alt="${u.title || ''}">` : (isSeries ? '📚' : '🏅')}
                        </div>
                        <div class="relative z-10 min-w-0">
                            <div class="flex items-center gap-1 mb-0.5">
                                <span class="text-xs text-amber-600 font-bold uppercase tracking-wide">Rank ${u.rank || idx + 1}</span>
                                <span class="text-[10px] px-1.5 py-0.5 rounded-full ${isSeries ? 'bg-indigo-100 text-indigo-600' : 'bg-green-100 text-green-600'} font-bold">
                                    ${isSeries ? 'Work' : 'User'}
                                </span>
                            </div>
                            <h3 class="font-bold text-gray-900 text-lg truncate">${u.title || 'Unknown'}</h3>
                            <div class="text-xs text-gray-500 font-medium">
                                ${locale.formatNumber(u.score || 0)} ${isSeries ? 'Views' : 'Pts'}
                            </div>
                        </div>
                        </div>
                    `;
        }).join('')}
            </div>
        `;

        return div;
    }
};
